import { createEvent, logOneDSDatapoint } from '../OneDsWrapper';
import type {
    CustomDataMap,
    AriaDatapointType,
    DataPointEventType,
    CoreAnalyticsOptions,
    PerformanceDatapointType,
} from 'owa-analytics-types';
import { DatapointStatus } from 'owa-analytics-types';
import type { IEventProperty } from '@microsoft/1ds-analytics-js';
import { EventLatency, ValueKind } from '@microsoft/1ds-analytics-js';
import optionallyTraceDatapoint from '../utils/optionallyTraceDatapoint';
import isDevOrAutomationEnvironment from '../utils/isDevOrAutomationEnvironment';
import { flattenData } from '../utils/flattenData';
import { matchEventDefinition } from '../unifiedTelemetry/eventsMapping';
import { addCommonUnifiedPropreties } from '../unifiedTelemetry/addCommonUnifiedPropreties';
import { ARIA_TENANT_ID, UNIFIED_TABLE_NAME } from 'owa-unified-telemetry';

import { AriaCoreDatapoint } from '../datapoints/AriaCoreDatapoint';
import { mergeEvents } from '../unifiedTelemetry/mergeEvents';
import { getUnifiedPerfCustomWaterfall } from '../unifiedTelemetry/getUnifiedPerfCustomWaterfall';
import { getUnifiedPerfProperties } from '../unifiedTelemetry/getUnifiedPerfProperties';
import { scrubForPii } from 'owa-config';
import type { AccountKey } from 'owa-client-types';

export async function logUnifiedEvents(
    datapoint: AriaDatapointType,
    analyticsOptions: CoreAnalyticsOptions,
    eventType: string,
    accountKey: AccountKey | 'globalAccount'
): Promise<void> {
    const bypassEventMatchingCheck = datapoint?.unifiedTelemetry?.bypassEventMatchingCheck;
    const unifiedEventName = bypassEventMatchingCheck
        ? datapoint?.unifiedTelemetry?.eventName
        : matchEventDefinition(datapoint.getEventName());
    if (!unifiedEventName || !isTargetEventType(eventType) || !datapoint.unifiedTelemetry) {
        return;
    }

    const counterpartEventName = datapoint.getEventName();
    // The name update is required here but shouldn't be allowed anywhere else.
    (datapoint as any).eventName = unifiedEventName;

    analyticsOptions.shouldIncludeUserInfoId = true;
    const commonProperties = await addCommonUnifiedPropreties(analyticsOptions, accountKey);
    const datapointProperties = datapoint.getProperties();

    // Set the counterpart event name to allow linking UT events back to their non-UT events
    commonProperties.CommonHVAProperties.CounterpartEventName = datapoint.getOptions()
        ?.unifiedTelemetryOnly
        ? 'NoCounterpart_UTOnlyEvent'
        : counterpartEventName;

    if ((datapoint as PerformanceDatapointType)?.isPerformanceDatapoint) {
        commonProperties.CommonHVAProperties.CommonPerfProperties =
            getUnifiedPerfProperties(datapointProperties);
    }

    const properties = mergeEvents(datapoint.unifiedTelemetry, commonProperties, () =>
        getUnifiedPerfCustomWaterfall(datapointProperties)
    );

    if ((datapoint as PerformanceDatapointType)?.isPerformanceDatapoint) {
        mapPerfStatusToActionSuccess(datapointProperties, properties);
    }

    if (commonProperties && properties) {
        const unifiedDatapoint = new AriaCoreDatapoint(
            datapoint.getEventName(),
            undefined /* MailboxInfo */,
            undefined /* extraCustomData */,
            undefined /* options */,
            flattenData(properties)
        );

        // If TableGrouping exists in UT definition, we use it as the table name,
        // else we fallback to UNIFIED_TABLE_NAME.
        const tableName = (properties.TableGrouping ?? UNIFIED_TABLE_NAME) as DataPointEventType;

        optionallyTraceDatapoint(unifiedDatapoint, tableName);

        // Do not log datapoints if we are gulping or in a branch or a tds box.
        if (!isDevOrAutomationEnvironment()) {
            const e = createEvent(
                tableName,
                unifiedDatapoint.getAllProperties(),
                datapoint.getCreationTime()
            );
            e.latency = EventLatency.Normal; // how quickly event is submitted to Aria
            if (e.data?.['HashedEmail']) {
                e.data['HashedEmail'] = <IEventProperty>{
                    value: e.data['HashedEmail'],
                    kind: ValueKind.Pii_Identity,
                };
            }

            logOneDSDatapoint({
                tenantToken: ARIA_TENANT_ID,
                item: e,
                analyticsOptions,
                accountKey: datapoint?.getAccountKey(),
                useUnifiedSchema: true,
            });
        }
    }
}

function isTargetEventType(eventType: string): boolean {
    return eventType === 'client_event' || eventType === 'client_cosmos';
}

function mapPerfStatusToActionSuccess(
    perfProperties: CustomDataMap,
    utProperties: CustomDataMap | null
) {
    if ('Status' in perfProperties && utProperties) {
        const resultStatus = perfProperties.Status as DatapointStatus;
        const isSuccess =
            resultStatus === DatapointStatus.Success ||
            resultStatus === DatapointStatus.BackgroundSuccess;
        utProperties.ActionSuccess = isSuccess;
        if (!isSuccess) {
            utProperties.ActionFailureInfo = resultStatus;
            if (
                'ErrorMessage' in perfProperties &&
                typeof perfProperties.ErrorMessage === 'string'
            ) {
                utProperties.VerboseFailureInfo = scrubForPii(perfProperties.ErrorMessage);
            }
        }
    }
}
